"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import {
  notificationService,
  useNotificationsPageQuery,
} from "@/src/features/notifications";
import ProfileDropdown from "@/src/components/layout/header/ProfileDropdown";
import { useAuth } from "@/src/core/auth/useAuth";
import Link from "next/link";
import InitialAvatar from "@/src/shared/components/atoms/InitialAvatar";

const ProfileSettings: React.FC = () => {
  const { user_profile } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { data } = useNotificationsPageQuery(
    user_profile?.id,
    1,
    !!user_profile?.id,
  );
  const notificationCount = notificationService.unreadCount(data?.data);

  // Profile rendering logic - prevent hydration mismatch
  const isAuthenticated = mounted ? !!user_profile : false;
  const avatarUrl = mounted ? user_profile?.image?.url || "" : "";
  const initial = mounted
    ? user_profile?.first_name
      ? user_profile.first_name.charAt(0).toUpperCase()
      : ""
    : "";

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleDropdownClose = () => {
    setIsDropdownOpen(false);
  };

  const showInitial = Boolean(mounted && isAuthenticated && !avatarUrl);
  const avatarWrapperClass = `w-8 h-8 rounded-full flex items-center justify-center overflow-hidden ${showInitial ? "bg-rose-500" : ""}`;
  const profileBtnClass = `flex items-center gap-2 border border-gray-300 px-2 py-1 rounded-2xl hover:shadow-md transition-shadow`;

  // Don't render profile content until mounted to prevent hydration mismatch
  if (!mounted) {
    return (
      <div className="flex items-center gap-4">
        <Link
          href="/earn-by-hosting"
          className="text-sm font-medium hover:bg-gray-100 px-4 py-3 rounded-full transition-colors whitespace-nowrap"
        >
          Earn By hosting
        </Link>
        <div className="relative">
          <button className={profileBtnClass} type="button">
            <Menu size={16} className="text-ink-secondary" aria-hidden="true" />
            <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
              <img alt="user" src="/deskImages/user.svg" />
            </div>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-4">
      <Link
        href="/earn-by-hosting"
        className="text-sm font-medium hover:bg-gray-100 px-4 py-3 rounded-full transition-colors whitespace-nowrap"
      >
        Earn By hosting
      </Link>

      <div className="relative">
        <button
          onClick={handleDropdownToggle}
          className={profileBtnClass}
          type="button"
          aria-label="Open menu"
        >
          {/* Menu icon on the left */}
          <Menu size={16} className="text-ink-secondary" aria-hidden="true" />
          {/* Avatar on the right; spacing handled by gap on the button */}
          <div className={avatarWrapperClass}>
            {isAuthenticated ? (
              avatarUrl ? (
                <img
                  alt="user"
                  src={avatarUrl}
                  className="w-full h-full object-cover"
                />
              ) : (
                <InitialAvatar title={initial} />
              )
            ) : (
              <img alt="user" src="/deskImages/user.svg" />
            )}
          </div>
        </button>
        <ProfileDropdown
          isOpen={isDropdownOpen}
          onClose={handleDropdownClose}
          notificationCount={notificationCount}
        />
      </div>
    </div>
  );
};

export default ProfileSettings;
